import { ref, watch } from 'vue'

/**
 * Panel UI state (collapse/expand, active tab).
 * Persisted to localStorage under `dz:chat:panelState`.
 */

/** Single source of truth for the panel tabs — used by the Tab type, the
 *  localStorage validation, and ChatPanel's tabList. Add new tabs here only. */
export const TAB_KEYS = ['global', 'dms', 'friends', 'staff', 'inbox', 'online'] as const
type Tab = typeof TAB_KEYS[number]

/** Width (px) of the desktop online-strip that hugs the chat panel's left
 *  edge. Folded into --chat-panel-width so every layout consumer reserves
 *  room for it (see syncCssVar). */
const STRIP_W = 52

const STORAGE_KEY = 'dz:chat:panelState'

const isOpen = ref(true)
const activeTab = ref<Tab>('global')
/** Which DM thread is open in the DMs/Inbox panes — shared so the Friends
 *  tab can trigger "open DM with user X" and land on the right view. */
const activeDMThreadId = ref<string | null>(null)
let loaded = false

function loadFromStorage() {
    if (loaded || typeof window === 'undefined') return
    loaded = true
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return
        const parsed = JSON.parse(raw)
        if (typeof parsed.isOpen === 'boolean') isOpen.value = parsed.isOpen
        if (typeof parsed.activeTab === 'string' && (TAB_KEYS as readonly string[]).includes(parsed.activeTab)) {
            activeTab.value = parsed.activeTab as Tab
        }
    } catch { /* noop */ }
}

function persist() {
    if (typeof window === 'undefined') return
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            isOpen: isOpen.value,
            activeTab: activeTab.value
        }))
    } catch { /* noop */ }
}

/**
 * Push the panel-width CSS var so main content can pad correctly.
 *
 * Desktop reserves the panel (360 open / 56 collapsed) PLUS the always-present
 * online strip (STRIP_W) that hugs the panel's left edge. Mobile (<768px)
 * renders neither — ChatPanel switches to a FAB + full-screen dialog — so it
 * reserves nothing, otherwise the layout keeps dead space on the right and the
 * GlobalAudioPlayer gets squeezed into a thin strip.
 *
 * Exported (via useChatUI) and idempotent BECAUSE ChatPanel calls it on mount:
 * ChatPanel clears the var on unmount (e.g. hidden on /onboarding), and the
 * `wired`-latched watchers below only fire on `isOpen` CHANGES — so a
 * remount without a toggle would otherwise leave the var cleared and collapse
 * the layout's right gutter. Re-applying on mount closes that gap.
 */
export function syncChatPanelWidth() {
    if (typeof window === 'undefined') return
    const mobile = window.matchMedia('(max-width: 768px)').matches
    const w = mobile ? '0px' : ((isOpen.value ? 360 : 56) + STRIP_W) + 'px'
    document.documentElement.style.setProperty('--chat-panel-width', w)
}

let wired = false
function wireWatchers() {
    if (wired) return
    wired = true
    watch([isOpen, activeTab], persist)

    // Keep the width var in sync with the two things that change it: the
    // open/collapsed toggle and crossing the mobile breakpoint.
    if (typeof window !== 'undefined') {
        const mq = window.matchMedia('(max-width: 768px)')
        watch(isOpen, syncChatPanelWidth, { immediate: true })
        // `addEventListener('change', ...)` is preferred over the
        // deprecated `addListener` — all modern browsers support it.
        mq.addEventListener('change', syncChatPanelWidth)
    }
}

export function useChatUI() {
    loadFromStorage()
    wireWatchers()

    return {
        isOpen,
        activeTab,
        activeDMThreadId,
        toggle: () => { isOpen.value = !isOpen.value },
        open: () => { isOpen.value = true },
        close: () => { isOpen.value = false },
        setTab: (t: Tab) => { activeTab.value = t; isOpen.value = true },
        openDMThread: (threadId: string | null) => { activeDMThreadId.value = threadId },
    }
}
